import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { config } from '../config';
import { trackApiError, trackEvent } from './appInsights';
import { TENANT_INACTIVE_EVENT } from '../constants/authEvents';

let currentToken: string | null = null;
type TokenProviderOptions = {
  forceRefresh?: boolean;
};

let tokenProvider: ((options?: TokenProviderOptions) => Promise<string | null>) | null = null;
let forceRefreshNextToken = false;

type TokenRefreshMetrics = {
  forcedNextRequestCount: number;
  unauthorizedRetryCount: number;
  unauthorizedRetrySuccessCount: number;
  unauthorizedRetryFailureCount: number;
};

const tokenRefreshMetrics: TokenRefreshMetrics = {
  forcedNextRequestCount: 0,
  unauthorizedRetryCount: 0,
  unauthorizedRetrySuccessCount: 0,
  unauthorizedRetryFailureCount: 0,
};

export type ApiErrorCode =
  | 'bad_request'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'validation_error'
  | 'server_error'
  | 'network_error'
  | 'unknown_error';

export interface ApiErrorInfo {
  status: number | null;
  code: ApiErrorCode;
  message: string;
  isNetworkError: boolean;
  data?: unknown;
  url?: string;
  method?: string;
}

const getApiErrorCode = (status: number | null): ApiErrorCode => {
  if (status === null) return 'network_error';
  if (status === 400) return 'bad_request';
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 422) return 'validation_error';
  if (status >= 500) return 'server_error';
  return 'unknown_error';
};

const getDefaultErrorMessage = (status: number | null): string => {
  if (status === null) return 'Unable to connect to the server. Please check your connection and try again.';

  switch (status) {
    case 400:
      return 'The request is invalid. Please verify your input and try again.';
    case 401:
      return 'You need to sign in again to continue.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 409:
      return 'This action conflicts with existing data.';
    case 422:
      return 'Some fields are invalid. Please review and try again.';
    default:
      if (status >= 500) return 'The server encountered an error. Please try again shortly.';
      return 'An unexpected error occurred. Please try again.';
  }
};

const extractErrorMessage = (data: unknown, fallbackMessage: string): string => {
  if (data && typeof data === 'object') {
    const typedData = data as {
      message?: unknown;
      title?: unknown;
      detail?: unknown;
      error?: unknown;
      errors?: unknown;
    };

    // ASP.NET ValidationProblemDetails typically returns an `errors` object keyed by field.
    if (typedData.errors && typeof typedData.errors === 'object' && !Array.isArray(typedData.errors)) {
      const firstFieldErrors = Object.values(typedData.errors as Record<string, unknown[]>)
        .find((entry) => Array.isArray(entry) && entry.length > 0) as unknown[] | undefined;

      const firstError = firstFieldErrors?.[0];
      if (typeof firstError === 'string' && firstError.trim()) {
        return firstError;
      }
    }

    if (typeof typedData.message === 'string' && typedData.message.trim()) {
      return typedData.message;
    }

    if (typeof typedData.detail === 'string' && typedData.detail.trim()) {
      return typedData.detail;
    }

    if (typeof typedData.title === 'string' && typedData.title.trim()) {
      return typedData.title;
    }

    if (typeof typedData.error === 'string' && typedData.error.trim()) {
      return typedData.error;
    }
  }

  return fallbackMessage;
};

const isTenantInactiveResponse = (data: unknown): data is { error: string; message?: string } => {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const typedData = data as { error?: unknown };
  return typedData.error === 'tenant_inactive';
};

const notifyTenantInactive = (message?: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(TENANT_INACTIVE_EVENT, {
      detail: { message },
    })
  );
};

const trackTokenRefreshMetric = (eventName: string, properties?: Record<string, string>) => {
  trackEvent('auth_token_refresh', {
    eventName,
    ...properties,
  });
};

export const getApiErrorInfo = (error: unknown): ApiErrorInfo => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status ?? null;
    const fallbackMessage = getDefaultErrorMessage(status);
    const data = error.response?.data;

    return {
      status,
      code: getApiErrorCode(status),
      message: extractErrorMessage(data, fallbackMessage),
      isNetworkError: !error.response,
      data,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
    };
  }

  if (error instanceof Error) {
    return {
      status: null,
      code: 'unknown_error',
      message: error.message || 'An unexpected error occurred. Please try again.',
      isNetworkError: false,
    };
  }

  return {
    status: null,
    code: 'unknown_error',
    message: 'An unexpected error occurred. Please try again.',
    isNetworkError: false,
  };
};

// Helper function to decode and inspect JWT token claims
const decodeAndLogToken = (token: string, showFull: boolean = false) => {
  try {
    const decoded = jwtDecode<any>(token);
    
    // Extract role-related claims
    const roles = decoded.public_metadata_roles || decoded.roles || [];
    const tenantId = decoded.tenant_id || decoded.public_metadata_tenant_id || null;
    
    const claims = {
      ' User ID': decoded.sub,
      ' Email': decoded.email || 'N/A',
      ' Roles': roles.length > 0 ? roles : '⚠️ NO ROLES ASSIGNED',
      ' Tenant ID': tenantId || 'N/A',
      ' Session': decoded.sid || 'N/A',
      ' Expires': decoded.exp ? new Date(decoded.exp * 1000).toISOString() : 'N/A',
      ' Issuer': decoded.iss || 'N/A',
    };
    
    console.group('🔍 JWT Token Claims');
    Object.entries(claims).forEach(([key, value]) => {
      if (key === '🎭 Roles' && Array.isArray(value)) {
        console.log(`${key}:`, value);
      } else if (key === '🎭 Roles' && typeof value === 'string') {
        console.warn(`${key}:`, value);
      } else {
        console.log(`${key}:`, value);
      }
    });
    
    if (showFull) {
      console.log('📋 All Claims:', decoded);
    }
    console.groupEnd();
    
    return decoded;
  } catch (e) {
    console.error(' Failed to decode JWT token:', e);
    return null;
  }
};

// Request interceptor factory - adds auth token to requests
const createRequestInterceptor = () => {
  return async (config: InternalAxiosRequestConfig) => {
    if (tokenProvider) {
      try {
        const shouldForceRefresh = forceRefreshNextToken;
        if (shouldForceRefresh) {
          tokenRefreshMetrics.forcedNextRequestCount += 1;
          trackTokenRefreshMetric('forced_next_request', {
            forcedNextRequestCount: String(tokenRefreshMetrics.forcedNextRequestCount),
          });
        }

        const freshToken = await tokenProvider({ forceRefresh: forceRefreshNextToken });
        forceRefreshNextToken = false;
        currentToken = freshToken;
      } catch (error) {
        forceRefreshNextToken = false;
        console.warn('⚠️ Failed to retrieve fresh auth token from provider:', error);
      }
    }
    
    if (currentToken) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${currentToken}`;
    } else {
      if (config.headers && 'Authorization' in config.headers) {
        delete config.headers['Authorization'];
      }
    }
    return config;
  };
};

// Response interceptor factory - handles errors
const createResponseInterceptor = (instance: AxiosInstance) => {
  return {
    onFulfilled: (response: AxiosResponse) => {
      return response;
    },
    onRejected: async (error: any) => {
      const apiError = getApiErrorInfo(error);

      // Tenant logo 404 is the designed "no custom logo" signal (frontend falls
      // back to the favicon) — don't log or track it as an error.
      const isExpectedLogoNotFound =
        apiError.status === 404 && /\/api\/tenants\/[^/]+\/logo$/.test(apiError.url ?? '');
      if (isExpectedLogoNotFound) {
        return Promise.reject(error);
      }

      if (error.response) {
        trackApiError(apiError.status, apiError.url, apiError.method, apiError.message);
        console.group('❌ API Error Response');
        console.error('Status:', apiError.status, error.response.statusText);
        console.error('URL:', apiError.url);
        console.error('Method:', apiError.method);
        console.error('Code:', apiError.code);
        console.error('Message:', apiError.message);
        
        // Handle 401 Unauthorized
        if (error.response.status === 401) {
          console.error('🔒 401 Unauthorized - Token invalid or expired');
          console.error('Has Auth Header:', !!error.config?.headers?.Authorization);

          const hasRetriedWithForcedRefresh = Boolean(error.config?._retryWithFreshToken);

          if (!hasRetriedWithForcedRefresh && tokenProvider && error.config) {
            tokenRefreshMetrics.unauthorizedRetryCount += 1;
            trackTokenRefreshMetric('unauthorized_retry_attempt', {
              unauthorizedRetryCount: String(tokenRefreshMetrics.unauthorizedRetryCount),
            });

            try {
              const refreshedToken = await tokenProvider({ forceRefresh: true });
              currentToken = refreshedToken;

              if (refreshedToken) {
                tokenRefreshMetrics.unauthorizedRetrySuccessCount += 1;
                trackTokenRefreshMetric('unauthorized_retry_success', {
                  unauthorizedRetrySuccessCount: String(tokenRefreshMetrics.unauthorizedRetrySuccessCount),
                });

                error.config.headers = error.config.headers || {};
                error.config.headers.Authorization = `Bearer ${refreshedToken}`;
                error.config._retryWithFreshToken = true;
                return instance.request(error.config);
              }

              tokenRefreshMetrics.unauthorizedRetryFailureCount += 1;
              trackTokenRefreshMetric('unauthorized_retry_empty_token', {
                unauthorizedRetryFailureCount: String(tokenRefreshMetrics.unauthorizedRetryFailureCount),
              });
            } catch (refreshError) {
              tokenRefreshMetrics.unauthorizedRetryFailureCount += 1;
              trackTokenRefreshMetric('unauthorized_retry_error', {
                unauthorizedRetryFailureCount: String(tokenRefreshMetrics.unauthorizedRetryFailureCount),
              });
              console.warn('⚠️ Forced token refresh failed after 401:', refreshError);
            }
          }
          
          // Show token info if present
          if (currentToken) {
            console.error('Current token (first 50 chars):', currentToken.substring(0, 50) + '...');
            decodeAndLogToken(currentToken, true);
          }
        }
        
        // Handle 403 Forbidden
        if (error.response.status === 403) {
          console.error('🚫 403 Forbidden - Insufficient permissions');
          console.error('You lack the required role for this operation');

          if (isTenantInactiveResponse(error.response.data)) {
            notifyTenantInactive(error.response.data.message);
          }
          
          // Show current roles from token
          if (currentToken) {
            const decoded = jwtDecode<any>(currentToken);
            const roles = decoded.public_metadata_roles || decoded.roles || [];
            console.error('Current roles:', roles.length > 0 ? roles : '⚠️ NO ROLES');
            console.error('Required roles: "admin" or "tenant-admin"');
            
            if (roles.length === 0) {
              console.error('');
              console.error('🔧 FIX: Add role to Clerk Dashboard:');
              console.error('1. Go to https://dashboard.clerk.com/');
              console.error('2. Find your user');
              console.error('3. Add to public_metadata: { "roles": ["admin"] }');
              console.error('4. Sign out and sign back in');
            }
          }
        }
        
        console.error('Response Data:', apiError.data);
        console.groupEnd();
      } else {
        console.error('❌ Network or other error:', apiError.message);
      }
      return Promise.reject(error);
    }
  };
};

/**
 * Factory function to create an axios instance with custom baseURL
 * All instances share the same authentication token and interceptors
 * 
 * @param baseURL - Custom base URL for the axios instance
 * @returns Configured axios instance
 * 
 * @example
 * // Create API client with specific base URL
 * const api = createApiClient('https://api.example.com');
 * 
 * // Or use the default client exported below
 * import apiClient from './api';
 */
export const createApiClient = (baseURL: string): AxiosInstance => {
  const instance = axios.create({
    baseURL: baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add request interceptor
  instance.interceptors.request.use(
    createRequestInterceptor(),
    (error) => Promise.reject(error)
  );

  // Add response interceptor
  const responseInterceptor = createResponseInterceptor(instance);
  instance.interceptors.response.use(
    responseInterceptor.onFulfilled,
    responseInterceptor.onRejected
  );

  return instance;
};

// Default axios instance with standard base configuration
const apiClient: AxiosInstance = createApiClient(config.api.baseUrl);

// Function to set the auth token
export const setAuthToken = (token: string | null) => {
  currentToken = token;
};

// Allows wiring Clerk getToken() into request-time token retrieval.
export const setTokenProvider = (provider: ((options?: TokenProviderOptions) => Promise<string | null>) | null) => {
  tokenProvider = provider;
};

// Schedules a forced token refresh for the next API request only.
export const requestTokenRefresh = () => {
  forceRefreshNextToken = true;
};

// Returns a snapshot for local debugging and telemetry sanity checks.
export const getTokenRefreshMetrics = (): TokenRefreshMetrics => ({
  ...tokenRefreshMetrics,
});

// Export the decode function for use in other components
export const inspectToken = (token: string) => decodeAndLogToken(token, true);

// Helper to manually check current token
export const getCurrentTokenInfo = () => {
  if (currentToken) {
    console.log('Current token set:', true);
    decodeAndLogToken(currentToken, true);
  } else {
    console.log('No token currently set');
  }
};

export default apiClient;
